import React, { useState, forwardRef, useRef, useEffect, useMemo, useCallback, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiCalendar, FiX, FiChevronDown, FiChevronUp, FiMenu, FiMaximize, FiMinimize, FiChevronLeft, FiChevronRight, FiSearch } from 'react-icons/fi';
import { Document } from '@/data/mockData';
import { format } from 'date-fns';
import HTMLFlipBook from 'react-pageflip';
import { pdfjs } from 'react-pdf';
import api, { getProxiedPdfUrl } from '@/utils/api';
import { getCachedPdfEntry, setCachedPdfEntry } from '@/utils/pdfCacheDB';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  PageLayout,
  ContentLayout,
  MainContent,
  Sidebar,
  ToggleDrawerButton,
  SidebarHeader,
  SidebarTitle,
  DrawerCloseButton,
  SidebarContent,
  Header,
  BackButton,
  Title,
  DocumentCount,
  DocumentCard,
  DocumentInfo,
  DocumentTitle,
  DocumentMeta,
  MetaItem,
  FileSize,
  DocumentDescription,
  // DocumentActions, // <--- Added new styled component
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyText,
  AccordionSection,
  AccordionHeader,
  AccordionTitle,
  AccordionContent,
  FlipbookViewerContainer,
  FlipControlsPanel,
  FlipControlsRow,
  FlipSearchWrapper,
  FlipIconButton,
  FlipPageInfo,
  FlipPageInput,
  FlipSearchInput,
  FlipSearchPopover,
  FlipSearchResults,
  FlipSearchResultItem,
  FlipSearchResultTitle,
  FlipSearchResultSnippet,
  ExplorerWrapper,
  ExplorerContent,
  DocumentGrid,
} from './DocumentsPage.styles';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Top-level flipbook components so they have stable identity across DocumentsPage re-renders
const AnyFlipBook = HTMLFlipBook as unknown as ComponentType<any>;

type FlipLayoutMode = 'single' | 'double' | 'auto';

const rawFlipLayoutMode = (import.meta.env.VITE_FLIPBOOK_LAYOUT_MODE ?? 'auto') as string;

console.log("rawFlipLayoutMode", rawFlipLayoutMode);

const normalizedFlipLayoutMode = rawFlipLayoutMode.trim().toLowerCase();

const FLIPBOOK_LAYOUT_MODE: FlipLayoutMode =
  normalizedFlipLayoutMode === 'single' ||
  normalizedFlipLayoutMode === 'double' ||
  normalizedFlipLayoutMode === 'auto'
    ? (normalizedFlipLayoutMode as FlipLayoutMode)
    : 'auto';

    console.log("normalizedFlipLayoutMode", normalizedFlipLayoutMode);

if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.info('[DocumentsPage] Flipbook layout mode:', FLIPBOOK_LAYOUT_MODE);
}

const FlipPage = React.memo(
  forwardRef<HTMLDivElement, { src: string; w: number; h: number }>(({ src, w, h }, ref) => {
    return (
      <div
        ref={ref}
        className="page"
        style={{
          width: `${w}px`,
          height: `${h}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={src}
          alt="PDF page"
          style={{
            width: `${w}px`,
            height: `${h}px`,
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </div>
    );
  }),
);

const BlankPage = React.memo(
  forwardRef<HTMLDivElement, { w: number; h: number }>(({ w, h }, ref) => (
    <div
      ref={ref}
      className="page"
      style={{ width: `${w}px`, height: `${h}px`, background: '#f8fafc' }}
    />
  )),
);

type FlipbookCommonProps = {
  width: number;
  height: number;
};

type StaticFlipbookProps = FlipbookCommonProps & {
  images: string[];
  flipBookRef: any;
  onFlip?: (e: any) => void;
};

const StaticFlipbook = React.memo(
  ({ images, flipBookRef, onFlip, width, height }: StaticFlipbookProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [dims, setDims] = useState<{ w: number; h: number; portrait: boolean }>(
      { w: width, h: height, portrait: false },
    );

    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const aspect = width / height; // page aspect (single page)

      const fit = () => {
        const rect = el.getBoundingClientRect();
        const pad = 12; // prevent clipping with borders/shadows
        const availW = Math.max(0, rect.width - pad * 2);
        const availH = Math.max(0, rect.height - pad * 2);
        if (availW === 0 || availH === 0) return;

        // Two-page spread candidate
        const maxH_byW_spread = Math.floor(availW / (2 * aspect));
        const h2 = Math.max(300, Math.min(1400, Math.floor(Math.min(availH, maxH_byW_spread))));
        const w2 = Math.floor(h2 * aspect);

        // Single-page (portrait) candidate for auto mode
        const maxH_byW_single = Math.floor(availW / aspect);
        const h1FromWidth = Math.max(
          300,
          Math.min(1400, Math.floor(Math.min(availH, maxH_byW_single))),
        );
        const w1FromWidth = Math.floor(h1FromWidth * aspect);

        let usePortrait: boolean;
        let pageW: number;
        let pageH: number;

        if (FLIPBOOK_LAYOUT_MODE === 'single') {
          // Single-page mode: fit one page fully inside the available box (contain).
          // Maintain aspect ratio and ensure we never exceed availW/availH so it
          // doesn't get cut off on smaller screens.
          usePortrait = true;
          // Start by filling width
          let wSingle = availW;
          let hSingle = wSingle / aspect;
          // If height would overflow, limit by height instead
          if (hSingle > availH) {
            hSingle = availH;
            wSingle = hSingle * aspect;
          }
          pageW = Math.floor(wSingle);
          pageH = Math.floor(hSingle);
        } else if (FLIPBOOK_LAYOUT_MODE === 'double') {
          usePortrait = false;
          pageW = w2;
          pageH = h2;
        } else {
          // auto: on narrow screens, always prefer single-page portrait layout
          const isNarrow = availW <= 768;
          usePortrait = isNarrow ? true : h1FromWidth > h2;
          pageW = usePortrait ? w1FromWidth : w2;
          pageH = usePortrait ? h1FromWidth : h2;
        }

        setDims((d) =>
          d.w !== pageW || d.h !== pageH || d.portrait !== usePortrait
            ? { w: pageW, h: pageH, portrait: usePortrait }
            : d,
        );
      };

      const ro = new ResizeObserver(() => fit());
      ro.observe(el);
      // initial
      fit();
      return () => ro.disconnect();
    }, [width, height]);

    const isSingleLayout = FLIPBOOK_LAYOUT_MODE === 'single';
    // Only show a hard cover when we are in a non-portrait (spread) layout.
    // In single/portrait mode this keeps the visible page centered instead of
    // behaving like an off-center book cover.
    const showCover = !dims.portrait;

    const flipProps: any = {
      width: dims.w,
      height: dims.h,
      size: isSingleLayout ? 'fixed' : 'stretch',
      minWidth: 280,
      maxWidth: 1000,
      minHeight: 320,
      maxHeight: 1400,
      drawShadow: true,
      maxShadowOpacity: 0.2,
      usePortrait: dims.portrait,
      autoSize: !isSingleLayout,
      mobileScrollSupport: true,
      showCover,
    };

    const pageNodes: React.ReactNode[] = images.map((src, i) => (
      <FlipPage key={`p-${i + 1}`} src={src} w={dims.w} h={dims.h} />
    ));
    if (pageNodes.length < 2) {
      pageNodes.push(<BlankPage key="blank-1" w={dims.w} h={dims.h} />);
    }

    return (
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 12,
          overflow: 'hidden',
        }}
      >
        <AnyFlipBook
          ref={flipBookRef}
          {...flipProps}
          onFlip={onFlip}
          className="flipbook"
          style={{ margin: '0 auto' }}
        >
          {pageNodes}
        </AnyFlipBook>
      </div>
    );
  },
);

type LoadingFlipPlaceholderProps = FlipbookCommonProps & { progress?: number };

const LoadingFlipPlaceholder = React.memo(({ width, height, progress = 0 }: LoadingFlipPlaceholderProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const skeletonRef = useRef<HTMLDivElement | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number; portrait: boolean }>(
    { w: width, h: height, portrait: false },
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const aspect = width / height;

    const fit = () => {
      const rect = el.getBoundingClientRect();
      const pad = 12;
      const availW = Math.max(0, rect.width - pad * 2);
      const availH = Math.max(0, rect.height - pad * 2);
      if (availW === 0 || availH === 0) return;

      const maxH_byW_spread = Math.floor(availW / (2 * aspect));
      const h2 = Math.max(300, Math.min(1400, Math.floor(Math.min(availH, maxH_byW_spread))));
      const w2 = Math.floor(h2 * aspect);
      const maxH_byW_single = Math.floor(availW / aspect);
      const h1FromWidth = Math.max(
        300,
        Math.min(1400, Math.floor(Math.min(availH, maxH_byW_single))),
      );
      const w1FromWidth = Math.floor(h1FromWidth * aspect);

      let usePortrait: boolean;
      let pageW: number;
      let pageH: number;

      if (FLIPBOOK_LAYOUT_MODE === 'single') {
        // Match StaticFlipbook: fit a single page fully inside available box.
        usePortrait = true;
        let wSingle = availW;
        let hSingle = wSingle / aspect;
        if (hSingle > availH) {
          hSingle = availH;
          wSingle = hSingle * aspect;
        }
        pageW = Math.floor(wSingle);
        pageH = Math.floor(hSingle);
      } else if (FLIPBOOK_LAYOUT_MODE === 'double') {
        usePortrait = false;
        pageW = w2;
        pageH = h2;
      } else {
        const isNarrow = availW <= 768;
        usePortrait = isNarrow ? true : h1FromWidth > h2;
        pageW = usePortrait ? w1FromWidth : w2;
        pageH = usePortrait ? h1FromWidth : h2;
      }

      setDims((d) =>
        d.w !== pageW || d.h !== pageH || d.portrait !== usePortrait
          ? { w: pageW, h: pageH, portrait: usePortrait }
          : d,
      );
    };

    const ro = new ResizeObserver(() => fit());
    ro.observe(el);
    fit();
    return () => ro.disconnect();
  }, [width, height]);

  const skeletonW = dims.portrait ? dims.w : Math.min(1000, dims.w * 2);
  const skeletonH = dims.h;

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
      }}
    >
      <div
        ref={skeletonRef}
        className="skeleton"
        style={{
          width: skeletonW,
          height: skeletonH,
          borderRadius: 12,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {progress > 0 && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            backgroundColor: '#f0f0f0',
            zIndex: 100,
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              backgroundColor: '#667eea',
              transition: 'width 0.3s ease',
              boxShadow: '0 0 10px rgba(102, 126, 234, 0.5)',
            }} />
          </div>
        )}
      </div>
    </div>
  );
});

export const DocumentsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [pageAspect, setPageAspect] = useState<number | null>(null); // width/height
  const [pageImages, setPageImages] = useState<string[]>([]);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ pageIndex: number; snippet: string }[]>([]);
  const [pageTexts, setPageTexts] = useState<string[]>([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeSearchPage, setActiveSearchPage] = useState<number | null>(null);
  const [drawerSearchQuery, setDrawerSearchQuery] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  // flipbook remount is controlled per-document only

  const viewerRef = useRef<HTMLDivElement | null>(null);
  const flipBookRef = useRef<any>(null);
  const currentPageRef = useRef<number>(0);
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const numPagesRef = useRef<number>(0);
  const pdfCacheRef = useRef<Record<string, { images: string[]; texts: string[]; aspect: number }>>({});

  // Fetch documents from API
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const response = await api.get('/documents');
        const docs = response.data.data || [];
        setDocuments(docs);
        
        // Auto-expand all categories
        const categories = new Set<string>(docs.map((doc: Document) => doc.category));
        setExpandedCategories(categories);
        
        // Debug logging for production issues
        // console.log('[DocumentsPage] Fetched documents with categories:', Array.from(categories));
        // docs.forEach((doc: Document) => {
        //   if (doc.category === 'program_updates' || doc.category === 'knowledge_documents') {
        //     console.log(`[DocumentsPage] Found document with category: "${doc.category}" - Title: "${doc.title}"`);
        //   }
        // });
      } catch (error: any) {
        console.error('Error fetching documents:', error);
        toast.error(error.response?.data?.message || 'Failed to load documents');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const width = 720; // base width used to render page images; flipbook will scale
  const height = pageAspect ? Math.round(width / pageAspect) : 800; // base height used by flipbook; adapts to aspect

  const pagesSrc = useMemo(() => {
    if (pageImages.length === 0 || !pageAspect) return null;
    return pageImages.slice();
  }, [pageImages, pageAspect]);

  const numPages = pagesSrc?.length ?? 0;
  numPagesRef.current = numPages; // Keep ref in sync

  const handleFlip = useCallback((e: any) => {
    if (!flipBookRef.current) return;
    try {
      const flip = flipBookRef.current.pageFlip();
      const index = typeof flip.getCurrentPageIndex === 'function' ? flip.getCurrentPageIndex() : e?.data;
      if (typeof index === 'number' && !Number.isNaN(index)) {
        // Update ref to track current page
        currentPageRef.current = index;
        // Update DOM directly without state changes
        if (controlsRef.current) {
          const pageInfoEl = controlsRef.current.querySelector('[data-page-info]');
          const inputEl = controlsRef.current.querySelector('[data-page-input]') as HTMLInputElement;
          const prevBtn = controlsRef.current.querySelector('[data-prev-btn]') as HTMLButtonElement;
          const nextBtn = controlsRef.current.querySelector('[data-next-btn]') as HTMLButtonElement;
          
          if (pageInfoEl) pageInfoEl.textContent = `${index + 1} / ${numPagesRef.current}`;
          if (inputEl) {
            inputEl.value = String(index + 1);
            inputEl.placeholder = String(index + 1);
          }
          if (prevBtn) prevBtn.disabled = index <= 0;
          if (nextBtn) nextBtn.disabled = index >= numPagesRef.current - 1;
        }
      }
    } catch {
      if (typeof e?.data === 'number') {
        currentPageRef.current = e.data;
        if (controlsRef.current) {
          const pageInfoEl = controlsRef.current.querySelector('[data-page-info]');
          const inputEl = controlsRef.current.querySelector('[data-page-input]') as HTMLInputElement;
          const prevBtn = controlsRef.current.querySelector('[data-prev-btn]') as HTMLButtonElement;
          const nextBtn = controlsRef.current.querySelector('[data-next-btn]') as HTMLButtonElement;
          
          if (pageInfoEl) pageInfoEl.textContent = `${e.data + 1} / ${numPagesRef.current}`;
          if (inputEl) {
            inputEl.value = String(e.data + 1);
            inputEl.placeholder = String(e.data + 1);
          }
          if (prevBtn) prevBtn.disabled = e.data <= 0;
          if (nextBtn) nextBtn.disabled = e.data >= numPagesRef.current - 1;
        }
      }
    }
  }, []);

  const updateControlsDOM = (index: number) => {
    if (controlsRef.current) {
      const pageInfoEl = controlsRef.current.querySelector('[data-page-info]');
      const inputEl = controlsRef.current.querySelector('[data-page-input]') as HTMLInputElement;
      const prevBtn = controlsRef.current.querySelector('[data-prev-btn]') as HTMLButtonElement;
      const nextBtn = controlsRef.current.querySelector('[data-next-btn]') as HTMLButtonElement;
      
      if (pageInfoEl) pageInfoEl.textContent = `${index + 1} / ${numPagesRef.current}`;
      if (inputEl) {
        inputEl.value = String(index + 1);
        inputEl.placeholder = String(index + 1);
      }
      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index >= numPagesRef.current - 1;
    }
  };

  const goToPrevPage = () => {
    if (!flipBookRef.current) return;
    flipBookRef.current.pageFlip().flipPrev();
    // Update controls after flip
    setTimeout(() => {
      const index = flipBookRef.current.pageFlip().getCurrentPageIndex();
      currentPageRef.current = index;
      updateControlsDOM(index);
    }, 0);
  };

  const goToNextPage = () => {
    if (!flipBookRef.current) return;
    flipBookRef.current.pageFlip().flipNext();
    // Update controls after flip
    setTimeout(() => {
      const index = flipBookRef.current.pageFlip().getCurrentPageIndex();
      currentPageRef.current = index;
      updateControlsDOM(index);
    }, 0);
  };

  const goToPage = (page: number) => {
    if (!flipBookRef.current || !numPages) return;
    const clamped = Math.min(numPages, Math.max(1, Math.floor(page)));
    const index = clamped - 1;
    // Let the flipbook's onFlip event be the single source of truth
    flipBookRef.current.pageFlip().flip(index);
    // Update controls after flip
    setTimeout(() => {
      currentPageRef.current = index;
      updateControlsDOM(index);
    }, 0);
  };


  const updateSearchResults = (value: string) => {
    const query = value.trim().toLowerCase();
    if (!query || pageTexts.length === 0) {
      setSearchResults([]);
      return;
    }

    const results: { pageIndex: number; snippet: string }[] = [];
    pageTexts.forEach((text, index) => {
      const lower = text.toLowerCase();
      const idx = lower.indexOf(query);
      if (idx !== -1) {
        const start = Math.max(0, idx - 40);
        const end = Math.min(text.length, idx + value.length + 60);
        const snippet = `${start > 0 ? '…' : ''}${text
          .slice(start, end)
          .replace(/\s+/g, ' ')
          .trim()}${end < text.length ? '…' : ''}`;
        results.push({ pageIndex: index, snippet });
      }
    });

    setSearchResults(results);
  };

  const handleSearchInputChange = (value: string) => {
    setSearchQuery(value);
    setActiveSearchPage(null);
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }
    updateSearchResults(value);
  };

  const handleSearchResultClick = (pageIndex: number) => {
    goToPage(pageIndex + 1);
    setActiveSearchPage(pageIndex);
  };

  const toggleFullscreen = () => {
    const el = viewerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      // Rely on fullscreenchange event to update state
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  // Keep isFullscreen in sync if user exits via ESC or browser UI
  useEffect(() => {
    const sync = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', sync);
    // vendor prefixes for older browsers
    document.addEventListener('webkitfullscreenchange' as any, sync as any);
    document.addEventListener('mozfullscreenchange' as any, sync as any);
    document.addEventListener('MSFullscreenChange' as any, sync as any);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange' as any, sync as any);
      document.removeEventListener('mozfullscreenchange' as any, sync as any);
      document.removeEventListener('MSFullscreenChange' as any, sync as any);
    };
  }, []);

  // Load PDF via pdfjs and render to images when a document is selected
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!selectedDocument?.fileUrl) return;
      try {
        // First, check in-memory cache to avoid re-downloading and re-rendering
        const cacheKey = selectedDocument._id;
        const cached = pdfCacheRef.current[cacheKey];
        if (cached) {
          if (cancelled) return;
          setPageAspect(cached.aspect);
          setPageImages(cached.images);
          setPageTexts(cached.texts);
          setSearchResults([]);
          setSearchQuery('');
          setIsSearchOpen(false);
          setActiveSearchPage(null);
          setLoadingProgress(0);
          setIsLoadingPages(false);
          // Re-initialize controls based on cached page count
          const totalPages = cached.images.length;
          setTimeout(() => {
            if (controlsRef.current) {
              const prevBtn = controlsRef.current.querySelector('[data-prev-btn]') as HTMLButtonElement;
              const nextBtn = controlsRef.current.querySelector('[data-next-btn]') as HTMLButtonElement;
              const inputEl = controlsRef.current.querySelector('[data-page-input]') as HTMLInputElement;
              if (prevBtn) prevBtn.disabled = true;
              if (nextBtn) nextBtn.disabled = totalPages <= 1;
              if (inputEl) {
                inputEl.value = '1';
                inputEl.placeholder = '1';
              }
            }
          }, 0);
          return;
        }

        // Then check persistent IndexedDB cache
        const persisted = await getCachedPdfEntry(cacheKey);
        if (persisted) {
          if (cancelled) return;
          pdfCacheRef.current[cacheKey] = {
            images: persisted.images,
            texts: persisted.texts,
            aspect: persisted.aspect,
          };
          setPageAspect(persisted.aspect);
          setPageImages(persisted.images);
          setPageTexts(persisted.texts);
          setSearchResults([]);
          setSearchQuery('');
          setIsSearchOpen(false);
          setActiveSearchPage(null);
          setLoadingProgress(0);
          setIsLoadingPages(false);
          const totalPages = persisted.images.length;
          setTimeout(() => {
            if (controlsRef.current) {
              const prevBtn = controlsRef.current.querySelector('[data-prev-btn]') as HTMLButtonElement;
              const nextBtn = controlsRef.current.querySelector('[data-next-btn]') as HTMLButtonElement;
              const inputEl = controlsRef.current.querySelector('[data-page-input]') as HTMLInputElement;
              if (prevBtn) prevBtn.disabled = true;
              if (nextBtn) nextBtn.disabled = totalPages <= 1;
              if (inputEl) {
                inputEl.value = '1';
                inputEl.placeholder = '1';
              }
            }
          }, 0);
          return;
        }

        setIsLoadingPages(true);
        // Use proxied URL to avoid CORS issues
        const proxiedUrl = getProxiedPdfUrl(selectedDocument._id);
        const loadingTask = pdfjs.getDocument(proxiedUrl as any);
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        const imgs: string[] = [];
        const texts: string[] = [];
        let aspect: number | null = null;
        const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
        const renderScale = Math.min(3, Math.max(devicePixelRatio, 2));
        for (let i = 1; i <= pdf.numPages; i++) {
          // Update progress bar
          const progress = Math.round((i / pdf.numPages) * 100);
          setLoadingProgress(progress);
          
          const page = await pdf.getPage(i);
          if (!aspect) {
            const vp1 = page.getViewport({ scale: 1 });
            aspect = vp1.width / vp1.height;
            setPageAspect(aspect);
          }
          try {
            const textContent = await page.getTextContent();
            const pageText = (textContent.items || [])
              .map((item: any) => (item && (item as any).str) || '')
              .join(' ');
            texts.push(pageText);
          } catch {
            texts.push('');
          }
          const baseW = width * renderScale;
          const vp1 = page.getViewport({ scale: 1 });
          const scale = baseW / vp1.width;
          const vp = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) continue;
          canvas.width = vp.width;
          canvas.height = vp.height;
          await (page as any).render({ canvasContext: ctx, viewport: vp }).promise;
          imgs.push(canvas.toDataURL());
        }
        if (!cancelled) {
          setPageImages(imgs);
          setPageTexts(texts);
          setSearchResults([]);
          setSearchQuery('');
          setIsSearchOpen(false);
          setActiveSearchPage(null);
          setLoadingProgress(0);
          setIsLoadingPages(false);
          // Cache rendered images and text for this document to avoid re-downloads
          const resolvedAspect = aspect ?? pageAspect ?? width / height;
          pdfCacheRef.current[cacheKey] = {
            images: imgs,
            texts,
            aspect: resolvedAspect,
          };
          setCachedPdfEntry(cacheKey, {
            images: imgs,
            texts,
            aspect: resolvedAspect,
          });
          // Initialize button disabled states and input value
          setTimeout(() => {
            if (controlsRef.current) {
              const prevBtn = controlsRef.current.querySelector('[data-prev-btn]') as HTMLButtonElement;
              const nextBtn = controlsRef.current.querySelector('[data-next-btn]') as HTMLButtonElement;
              const inputEl = controlsRef.current.querySelector('[data-page-input]') as HTMLInputElement;
              if (prevBtn) prevBtn.disabled = true; // First page, prev disabled
              if (nextBtn) nextBtn.disabled = imgs.length <= 1; // Next disabled if only 1 page
              if (inputEl) {
                inputEl.value = '1'; // Start at page 1
                inputEl.placeholder = '1';
              }
            }
          }, 0);
        }
      } catch {}
    };
    // Reset before load
    currentPageRef.current = 0;
    setPageAspect(null);
    setPageImages([]);
    setPageTexts([]);
    setSearchResults([]);
    setSearchQuery('');
    setIsSearchOpen(false);
    setActiveSearchPage(null);
    setIsLoadingPages(true);
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedDocument]);
  // Helper function to humanize category names
  const humanizeCategory = (category: string): string => {
    if (!category) return 'Other';
    
    const humanized = category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    // Debug logging for production issues
    if (category === 'program_updates' || category === 'knowledge_documents') {
      console.log(`[DocumentsPage] Humanizing: "${category}" -> "${humanized}"`);
    }
    
    return humanized;
  };

  // Group documents by category
  const documentsByCategory = documents.reduce((acc, doc) => {
    if (!acc[doc.category]) {
      acc[doc.category] = [];
    }
    acc[doc.category].push(doc);
    return acc;
  }, {} as Record<string, Document[]>);

  // Filter documents based on drawer search query
  const filteredDocumentsByCategory = Object.entries(documentsByCategory).reduce((acc, [category, docs]) => {
    const filtered = docs.filter(doc =>
      doc.title.toLowerCase().includes(drawerSearchQuery.toLowerCase()) ||
      doc.description?.toLowerCase().includes(drawerSearchQuery.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[category] = filtered;
    }
    return acc;
  }, {} as Record<string, Document[]>);

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handleDocumentClick = (doc: Document) => {
    const isPdf = doc.fileType === 'application/pdf' || doc.fileType === 'pdf';
    if (doc.fileUrl && isPdf) {
      setSelectedDocument(doc);
      setPageAspect(null);
      setPageImages([]);
      // Auto-close drawer when document is opened
      setIsDrawerOpen(false);
    } else {
      alert(`Opening: ${doc.title}\n\nThis document type is not yet supported for preview.`);
    }
  };

  const renderExplorerView = () => {
    return (
      <ExplorerWrapper>
        <ExplorerContent>
          <div style={{ marginBottom: 'clamp(1rem, 4vw, 1.5rem)' }}>
            <input
              type="text"
              placeholder="Search documents..."
              value={drawerSearchQuery}
              onChange={(e) => setDrawerSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #cbd5e0',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#cbd5e0';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            <p
              style={{
                marginTop: '0.5rem',
                fontSize: '0.75rem',
                color: '#718096',
              }}
            >
              Search and browse documents by category. Select a PDF document to open it in the flipbook viewer.
            </p>
          </div>

          {Object.keys(filteredDocumentsByCategory).length === 0 ? (
            <EmptyState>
              <EmptyIcon>📄</EmptyIcon>
              <EmptyTitle>No documents found</EmptyTitle>
              <EmptyText>Try a different keyword or clear the search to see all documents.</EmptyText>
            </EmptyState>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {Object.entries(filteredDocumentsByCategory).map(([category, documents]) => (
                <AccordionSection key={category}>
                  <AccordionHeader
                    onClick={() => toggleCategory(category)}
                    $isExpanded={expandedCategories.has(category)}
                  >
                    <AccordionTitle>
                      {humanizeCategory(category)}
                      <DocumentCount>{documents.length}</DocumentCount>
                    </AccordionTitle>
                    {expandedCategories.has(category) ? <FiChevronUp /> : <FiChevronDown />}
                  </AccordionHeader>

                  <AnimatePresence>
                    {expandedCategories.has(category) && (
                      <AccordionContent
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <DocumentGrid>
                          {documents.map((doc) => (
                            <DocumentCard
                              key={doc._id}
                              onClick={() => handleDocumentClick(doc)}
                              whileTap={{ scale: 0.98 }}
                              isSelected={selectedDocument?._id === doc._id}
                            >
                              <DocumentInfo>
                                <DocumentTitle>{doc.title}</DocumentTitle>
                                {doc.description && (
                                  <DocumentDescription>{doc.description}</DocumentDescription>
                                )}
                                <DocumentMeta>
                                  <MetaItem>
                                    <FiCalendar />
                                    {format(new Date(doc.createdAt), 'MMM dd, yyyy')}
                                  </MetaItem>
                                  {/* <MetaItem>
                                    {user?.role === 'program_manager' && doc.fileUrl && (
                                      <DocumentActions>
                                        <a
                                          href={getProxiedPdfUrl(doc._id)}
                                          download
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <FiDownload /> PDF
                                        </a>
                                      </DocumentActions>
                                    )}
                                    {user?.role !== 'program_manager' && (doc.fileType.split('/').pop()?.toUpperCase() || 'FILE')}
                                  </MetaItem> */}
                                  <MetaItem>
                                    {user?.role === 'program_manager' && doc.fileUrl && (
                                    <FileSize>
                                      {doc.fileSize
                                        ? `${(doc.fileSize / (1024 * 1024)).toFixed(2)} MB`
                                        : 'Unknown size'}
                                    </FileSize>
                                    )}
                                  </MetaItem>
                                </DocumentMeta>
                                
                              </DocumentInfo>
                            </DocumentCard>
                          ))}
                        </DocumentGrid>
                      </AccordionContent>
                    )}
                  </AnimatePresence>
                </AccordionSection>
              ))}
            </div>
          )}
        </ExplorerContent>
      </ExplorerWrapper>
    );
  };

  if (loading) {
    return (
      <PageLayout>
        <Header>
          <BackButton variant="outline" onClick={() => navigate('/')}>
            <FiArrowLeft /> Back
          </BackButton>
          <Title>Documents Hub</Title>
        </Header>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Header>
        <BackButton variant="outline" onClick={() => navigate('/')}>
          <FiArrowLeft /> Back
        </BackButton>
        <Title>Documents Hub</Title>
        <DocumentCount>
          {documents.length} {documents.length === 1 ? 'Document' : 'Documents'}
        </DocumentCount>
        {selectedDocument && selectedDocument.fileUrl && (
          <ToggleDrawerButton 
            variant="outline" 
            onClick={() => setIsDrawerOpen(!isDrawerOpen)}
            aria-label={isDrawerOpen ? 'Hide Documents' : 'Show Documents'}
          >
            <FiMenu />
          </ToggleDrawerButton>
        )}
        {selectedDocument?.url && (
          <ToggleDrawerButton
            variant="outline"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
            title={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
          >
            {isFullscreen ? <FiMinimize /> : <FiMaximize />}
          </ToggleDrawerButton>
        )}
      </Header>

        <ContentLayout>
        {/* Main Content Area - Flipbook or Explorer */}
        <MainContent>
          {selectedDocument && selectedDocument.fileUrl ? (
            <FlipbookViewerContainer ref={viewerRef}>
              {isLoadingPages || !pagesSrc ? (
                <LoadingFlipPlaceholder width={width} height={height} progress={loadingProgress} />
              ) : (
                <>
                  <StaticFlipbook
                    images={pagesSrc}
                    flipBookRef={flipBookRef}
                    onFlip={handleFlip}
                    width={width}
                    height={height}
                  />
                  <FlipControlsPanel ref={controlsRef}>
                    <FlipSearchWrapper>
                      <FlipIconButton
                        onClick={() => setIsSearchOpen((open) => !open)}
                        disabled={isLoadingPages || !numPages}
                        title="Search in document"
                        aria-label="Search in document"
                      >
                        <FiSearch />
                      </FlipIconButton>

                      <AnimatePresence>
                        {isSearchOpen && (
                          <FlipSearchPopover
                            initial={{ opacity: 0, x: 16 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 16 }}
                          >
                            <FlipControlsRow>
                              <FlipSearchInput
                                type="text"
                                placeholder="Search in document..."
                                value={searchQuery}
                                onChange={(e) => handleSearchInputChange(e.target.value)}
                                autoFocus
                              />
                            </FlipControlsRow>

                            {searchResults.length > 0 ? (
                              <FlipSearchResults>
                                {searchResults.map((result, index) => (
                                  <FlipSearchResultItem
                                    key={`${result.pageIndex}-${index}`}
                                    onClick={() => handleSearchResultClick(result.pageIndex)}
                                    $isActive={activeSearchPage === result.pageIndex}
                                  >
                                    <FlipSearchResultTitle>
                                      Page {result.pageIndex + 1}
                                    </FlipSearchResultTitle>
                                    <FlipSearchResultSnippet>
                                      {result.snippet}
                                    </FlipSearchResultSnippet>
                                  </FlipSearchResultItem>
                                ))}
                              </FlipSearchResults>
                            ) : (
                              searchQuery.trim() && (
                                <FlipPageInfo>No results found</FlipPageInfo>
                              )
                            )}
                          </FlipSearchPopover>
                        )}
                      </AnimatePresence>
                    </FlipSearchWrapper>

                    <FlipPageInfo data-page-info>
                      {numPages ? `${currentPageRef.current + 1} / ${numPages}` : '– / –'}
                    </FlipPageInfo>

                    <FlipIconButton
                      data-prev-btn
                      onClick={goToPrevPage}
                      title="Previous page"
                      aria-label="Previous page"
                    >
                      <FiChevronLeft />
                    </FlipIconButton>

                    <FlipPageInput
                      data-page-input
                      type="number"
                      min={1}
                      max={numPages || undefined}
                      defaultValue="1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const input = e.currentTarget as HTMLInputElement;
                          const page = parseInt(input.value, 10);
                          if (!Number.isNaN(page)) {
                            goToPage(page);
                          }
                        }
                      }}
                      placeholder={numPages ? String(currentPageRef.current + 1) : '–'}
                      title="Go to page (press Enter)"
                    />

                    <FlipIconButton
                      data-next-btn
                      onClick={goToNextPage}
                      title="Next page"
                      aria-label="Next page"
                    >
                      <FiChevronRight />
                    </FlipIconButton>
                  </FlipControlsPanel>
                </>
              )}
            </FlipbookViewerContainer>
          ) : (
            renderExplorerView()
          )}
        </MainContent>

        {/* Right Drawer - Collapsible */}
        <AnimatePresence>
          {isDrawerOpen && selectedDocument && selectedDocument.fileUrl && (
            <Sidebar
              initial={{ x: 400 }}
              animate={{ x: 0 }}
              exit={{ x: 400 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
          <SidebarHeader>
            <SidebarTitle>All Documents</SidebarTitle>
            <DrawerCloseButton
              onClick={() => setIsDrawerOpen(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Close drawer"
            >
              <FiX />
            </DrawerCloseButton>
          </SidebarHeader>

          <div style={{ padding: '1rem', borderBottom: '1px solid #e2e8f0' }}>
            <input
              type="text"
              placeholder="Search documents..."
              value={drawerSearchQuery}
              onChange={(e) => setDrawerSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #cbd5e0',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#cbd5e0';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
          
          <SidebarContent>
            {Object.entries(filteredDocumentsByCategory).map(([category, documents]) => (
              <AccordionSection key={category}>
                <AccordionHeader
                  onClick={() => toggleCategory(category)}
                  $isExpanded={expandedCategories.has(category)}
                >
                  <AccordionTitle>
                    {humanizeCategory(category)}
                    <DocumentCount>{documents.length}</DocumentCount>
                  </AccordionTitle>
                  {expandedCategories.has(category) ? <FiChevronUp /> : <FiChevronDown />}
                </AccordionHeader>
                
                <AnimatePresence>
                  {expandedCategories.has(category) && (
                    <AccordionContent
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {documents.map((doc) => (
                        <DocumentCard
                          key={doc._id}
                          onClick={() => handleDocumentClick(doc)}
                          whileTap={{ scale: 0.98 }}
                          isSelected={selectedDocument?._id === doc._id}
                        >
                          {/* <FileIcon $fileType={doc.fileType}>
                            <FiFile />
                          </FileIcon> */}
                          <DocumentInfo>
                            <DocumentTitle>{doc.title}</DocumentTitle>
                            <DocumentMeta>
                              <MetaItem>
                                <FiCalendar />
                                {format(new Date(doc.createdAt), 'MMM dd, yyyy')}
                              </MetaItem>
                              <MetaItem>
                                {/* <FiDownload /> */}
                                {/* {doc.fileType.split('/').pop()?.toUpperCase() || 'FILE'} */}
                              </MetaItem>
                              {/* <MetaItem>
                                {user?.role === 'program_manager' && doc.fileUrl && (
                                  <DocumentActions>
                                    <a
                                      href={getProxiedPdfUrl(doc._id)}
                                      download
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <FiDownload /> PDF
                                    </a>
                                  </DocumentActions>
                                )}
                                {user?.role !== 'program_manager' && (doc.fileType.split('/').pop()?.toUpperCase() || 'FILE')}
                              </MetaItem> */}
                              <MetaItem>
                                <FileSize>
                                  {doc.fileSize
                                    ? `${(doc.fileSize / (1024 * 1024)).toFixed(2)} MB`
                                    : 'Unknown size'}
                                </FileSize>
                              </MetaItem>
                            </DocumentMeta>
                          </DocumentInfo>
                        </DocumentCard>
                      ))}
                    </AccordionContent>
                  )}
                </AnimatePresence>
              </AccordionSection>
            ))}
          </SidebarContent>
            </Sidebar>
          )}
        </AnimatePresence>
      </ContentLayout>
    </PageLayout>
  );
};
